import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import { colors, PCProvider } from "@permisso/permisso-components";
import { ReactNode } from "react";
import { getColorWithOpacity } from "src/apps/config/colors";
import { CSS_RESET } from "src/apps/config/css-reset";
import { useTheme } from "src/apps/providers/ThemeStore";
import { ThemeProvider, createGlobalStyle } from "styled-components";
import { LETTER_SPACING } from "../config/constants";
import { SHADOWS } from "../config/shadows";
import { mapToPCTheme } from "./mapToPCTheme";

export const CustomizationProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const pcTheme = mapToPCTheme(theme);

  const chakraTheme = extendTheme({
    styles: {
      global: () => ({
        body: {
          bg: "",
        },
      }),
    },
    fonts: {
      heading: theme.fontFamilyHeadings,
      body: theme.fontFamily,
    },
    components: {
      Button: {
        baseStyle: {
          fontWeight: "500",
        },
        variants: {
          solid: {
            bg: theme.primaryButtonBackgroundColor || theme.primaryColor,
            color: theme.primaryButtonTextColor || colors.grey0,
            _disabled:
              theme.primaryButtonDisabledBackgroundColor && theme.primaryButtonDisabledTextColor
                ? {
                    bg: theme.primaryButtonDisabledBackgroundColor,
                    color: theme.primaryButtonDisabledTextColor,
                    opacity: 1,
                  }
                : {
                    opacity: 0.5,
                  },
            "&:hover": {
              bg: theme.primaryButtonBackgroundColor || theme.primaryColor,
              shadow: SHADOWS.md,
            },
            "&:hover:disabled": {
              bg: theme.primaryButtonDisabledBackgroundColor || theme.primaryColor,
              color: theme.primaryButtonDisabledTextColor || colors.grey0,
              shadow: "unset",
            },
          },
          outline: {
            bg: colors.grey0,
            borderColor: theme.primaryButtonBackgroundColor || theme.primaryColor,
            color: theme.primaryButtonBackgroundColor || theme.primaryColor,
            _disabled: {
              opacity: 0.5,
            },
            "&:hover": {
              bg: theme.primaryButtonBackgroundColor || theme.primaryColor,
              shadow: SHADOWS.md,
              color: theme.primaryButtonTextColor || colors.grey0,
            },
            "&:hover:disabled": {
              bg: colors.grey0,
              shadow: "unset",
              color: theme.primaryButtonBackgroundColor || theme.primaryColor,
            },
          },
          ghost: {
            bg: colors.grey0,
            color: colors.grey700,
            _disabled: {
              opacity: 0.5,
            },
            "&:hover": {
              bg: getColorWithOpacity(colors.grey700, 0.05),
            },
            "&:hover:disabled": {
              bg: "unset",
            },
          },
          danger: {
            bg: theme.errorColor,
            color: colors.grey0,
            _disabled: {
              opacity: 0.5,
            },
            "&:hover": {
              bg: theme.errorColor,
              shadow: SHADOWS.md,
            },
            "&:hover:disabled": {
              opacity: 0.5,
              bg: theme.errorColor,
            },
          },
        },
      },
    },
  });

  // fontLinks is stripped out (unused here) because PCProvider below renders the
  // <link> tags itself, from pcTheme.fontLinks — rendering them again here would
  // duplicate them.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { fontLinks, ...styleThemes } = theme;

  return (
    <PCProvider theme={pcTheme}>
      <ThemeProvider theme={styleThemes}>
        <ChakraProvider theme={chakraTheme}>{children}</ChakraProvider>
        <GlobalStyle />
      </ThemeProvider>
    </PCProvider>
  );
};

export const GlobalStyle = createGlobalStyle`
  ${CSS_RESET}
  
  html {
    font-family: ${({ theme }) => theme.fontFamily} !important;
    font-style: normal;
    font-weight: 400;
    letter-spacing: ${LETTER_SPACING};
    font-size: ${({ theme }) => theme.fontSize}px !important;
  }

  *:focus {
    // Removes default focus outline of chakra
    box-shadow: none !important;
  }

  // These are styles for the react phone input. We should remove this component when we get the chance
  .react-phone-input .form-control {
    border: 1px solid ${colors.grey400} ;
    width: 212px;
    padding-left: 60px;
    height: 32px ;
    font-size: 0.875rem;
    line-height: 20px ;
    color: ${({ theme }) => theme.blackColor} ;
    border-radius: 6px;
  }

  .react-phone-input .form-control:focus:focus-within {
    box-shadow: 3px 5px 10px rgba(0, 0, 0, 0.15);
    padding: 4px;
    padding-left: 60px;
  }

  .react-tel-input {
    font-family: ${({ theme }) => theme.fontFamily} !important;
    color: ${({ theme }) => theme.blackColor} !important;
  }

  .react-phone-input .form-control::placeholder {
    color: ${colors.grey400};
  }

  .react-phone-input .selected-flag {
    background-color: transparent ;
  }

  .react-phone-input .arrow {
    margin-left: 6px !important;
    border-left: 6px solid transparent !important;
    border-right: 6px solid transparent !important;
    border-top: 6px solid ${({ theme }) => theme.blackColor} !important;
  }

  .react-phone-input .up {
    // styled applied to the arrow when dropdown appears
    transform: rotate(180deg) !important;
    margin-top: -2px !important;
    border-bottom: none !important;
  }

  .react-phone-input .flag-dropdown {
    border: none;
    display: block;
    position: relative;
    top: -18px;
    margin-left: 4px;
  }

  .react-phone-input .search {
    padding: 4px 4px;
  }

  .react-phone-input .search-box {
    width: 100%;
    border: 1px solid ${colors.grey400} !important;
    height: 32px;
    border-radius: 8px !important;
    user-select: "text";
    -webkit-user-select: "text";
  }

  .react-phone-input .search-emoji {
    display: none;
  }

  .react-phone-input .country-list {
    padding-right: 12px;
  }
`;
